import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import HRHeader from "../components/HRHeader";
import api, { uploadFile, openFile } from "../api";
import { toast } from "sonner";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plane, Plus, X, CalendarIcon, MapPin, Paperclip, Trash2, Upload } from "lucide-react";

export const TRIP_STATUS = {
  in_attesa_responsabile: { label: "Attesa responsabile", cls: "bg-amber-100 text-amber-700" },
  in_attesa_hr: { label: "Attesa HR", cls: "bg-blue-100 text-blue-700" },
  approvata: { label: "Approvata", cls: "bg-green-100 text-green-700" },
  rifiutata: { label: "Rifiutata", cls: "bg-red-100 text-red-700" },
  annullata: { label: "Annullata", cls: "bg-gray-100 text-gray-500" },
};

export const euro = (v) => "\u20ac " + Number(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmt = (iso) => { try { return format(new Date(iso), "dd MMM yyyy", { locale: it }); } catch { return iso; } };

const CATEGORIES = ["Vitto", "Alloggio", "Trasporto", "Altro"];

const DatePicker = ({ value, onChange, placeholder }) => {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="w-full flex items-center gap-2 border border-gray-300 rounded-sm px-3 py-2.5 text-[14px] text-left hover:border-[#16345c] transition-colors">
          <CalendarIcon size={16} className="text-gray-400" />
          {value ? format(value, "dd/MM/yyyy") : <span className="text-gray-400">{placeholder}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="single" selected={value} onSelect={(d) => { onChange(d); setOpen(false); }} locale={it} />
      </PopoverContent>
    </Popover>
  );
};

const TrasfertePage = () => {
  const [list, setList] = useState([]);
  const [destination, setDestination] = useState("");
  const [start, setStart] = useState(null);
  const [end, setEnd] = useState(null);
  const [reason, setReason] = useState("");
  const newExpense = () => ({ uid: Math.random().toString(36).slice(2), category: "Vitto", description: "", amount: "", file: null });
  const [expenses, setExpenses] = useState([newExpense()]);
  const [submitting, setSubmitting] = useState(false);

  const load = () => api.get("/trips/mine").then((r) => setList(r.data)).catch((e) => { if (process.env.NODE_ENV !== "production") console.error(e); });
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const total = expenses.reduce((s, e) => s + (Number(e.amount) || 0), 0);

  const updateExp = (uid, patch) => setExpenses(expenses.map((e) => (e.uid === uid ? { ...e, ...patch } : e)));
  const addExp = () => setExpenses([...expenses, newExpense()]);
  const removeExp = (uid) => setExpenses(expenses.filter((e) => e.uid !== uid));

  const submit = async (e) => {
    e.preventDefault();
    if (!destination.trim()) { toast.error("Inserisci la destinazione"); return; }
    if (!start || !end) { toast.error("Seleziona le date"); return; }
    const valid = expenses.filter((x) => Number(x.amount) > 0);
    if (valid.length === 0) { toast.error("Aggiungi almeno una voce di spesa con importo"); return; }
    setSubmitting(true);
    try {
      const built = [];
      for (const x of valid) {
        let receipt_file_id = null;
        if (x.file) {
          const up = await uploadFile(x.file);
          receipt_file_id = up.id;
        }
        built.push({ category: x.category, description: x.description, amount: Number(x.amount), receipt_file_id });
      }
      await api.post("/trips", {
        destination, start_date: format(start, "yyyy-MM-dd"), end_date: format(end, "yyyy-MM-dd"),
        reason, estimated_amount: total, expenses: built,
      });
      toast.success("Trasferta inviata! In attesa di approvazione.");
      setDestination(""); setStart(null); setEnd(null); setReason("");
      setExpenses([newExpense()]);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore nell'invio");
    } finally { setSubmitting(false); }
  };

  const cancel = async (id) => {
    try { await api.put(`/trips/${id}/cancel`); toast.success("Trasferta annullata"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  return (
    <div className="min-h-screen bg-[#f0f2f5]">
      <HRHeader />
      <main className="max-w-[1200px] mx-auto px-4 py-8">
        <h1 className="text-[26px] font-black text-[#16345c] mb-1 flex items-center gap-2"><Plane size={24} /> Trasferte e Note Spese</h1>
        <p className="text-[14px] text-gray-500 mb-6">Inserisci le voci di spesa con allegati: il rimborso viene calcolato in automatico. Approvazione a cura del responsabile del reparto.</p>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Form */}
          <div className="lg:col-span-3">
            <div className="bg-white border border-gray-200 rounded-lg p-5">
              <h2 className="text-[16px] font-bold text-[#16345c] mb-4 flex items-center gap-2"><Plus size={18} /> Nuova trasferta</h2>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="block text-[12px] font-semibold text-gray-600 mb-1">Destinazione</label>
                  <div className="flex items-center border border-gray-300 rounded-sm focus-within:border-[#16345c]">
                    <span className="px-2.5 text-gray-400"><MapPin size={16} /></span>
                    <input value={destination} onChange={(e) => setDestination(e.target.value)} placeholder="es. Milano - Borsa Italiana" className="flex-1 py-2.5 pr-3 text-[14px] outline-none" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[12px] font-semibold text-gray-600 mb-1">Dal</label>
                    <DatePicker value={start} onChange={setStart} placeholder="Data" />
                  </div>
                  <div>
                    <label className="block text-[12px] font-semibold text-gray-600 mb-1">Al</label>
                    <DatePicker value={end} onChange={setEnd} placeholder="Data" />
                  </div>
                </div>
                <div>
                  <label className="block text-[12px] font-semibold text-gray-600 mb-1">Motivo</label>
                  <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="Motivo della trasferta..." className="w-full border border-gray-300 rounded-sm px-3 py-2 text-[14px] outline-none focus:border-[#16345c] resize-none" />
                </div>

                {/* Expenses */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-[12px] font-semibold text-gray-600">Voci di spesa</label>
                    <button type="button" onClick={addExp} data-testid="trip-add-expense" className="flex items-center gap-1 text-[12px] font-semibold text-[#16345c] hover:underline"><Plus size={14} /> Aggiungi voce</button>
                  </div>
                  <div className="space-y-2">
                    {expenses.map((x) => (
                      <div key={x.uid} className="grid grid-cols-12 gap-2 items-center bg-gray-50 rounded-sm p-2">
                        <div className="col-span-4 sm:col-span-3">
                          <Select value={x.category} onValueChange={(v) => updateExp(x.uid, { category: v })}>
                            <SelectTrigger className="h-9 text-[13px]"><SelectValue /></SelectTrigger>
                            <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <input value={x.description} onChange={(e) => updateExp(x.uid, { description: e.target.value })} placeholder="Descrizione" className="col-span-8 sm:col-span-4 border border-gray-300 rounded-sm px-2 py-1.5 text-[13px] outline-none focus:border-[#16345c]" />
                        <div className="col-span-5 sm:col-span-2 flex items-center border border-gray-300 rounded-sm bg-white focus-within:border-[#16345c]">
                          <span className="pl-2 text-gray-400 text-[13px]">€</span>
                          <input type="number" min="0" step="0.01" value={x.amount} onChange={(e) => updateExp(x.uid, { amount: e.target.value })} placeholder="0,00" className="w-full py-1.5 px-1 text-[13px] outline-none" />
                        </div>
                        <label className={`col-span-5 sm:col-span-2 flex items-center gap-1 border border-dashed rounded-sm px-2 py-1.5 text-[12px] cursor-pointer transition-colors ${x.file ? "border-[#16345c] text-[#16345c]" : "border-gray-300 text-gray-400 hover:border-[#16345c]"}`}>
                          <Upload size={13} className="shrink-0" />
                          <span className="truncate">{x.file ? x.file.name : "Allega"}</span>
                          <input type="file" accept="application/pdf,image/*" className="hidden" onChange={(e) => updateExp(x.uid, { file: e.target.files?.[0] || null })} />
                        </label>
                        <button type="button" onClick={() => removeExp(x.uid)} data-testid={`trip-remove-expense-${x.uid}`} className="col-span-2 sm:col-span-1 flex justify-center text-gray-400 hover:text-[#c00510] transition-colors"><Trash2 size={16} /></button>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-between mt-3 px-1">
                    <span className="text-[13px] font-semibold text-gray-600">Totale rimborso richiesto</span>
                    <span className="text-[18px] font-black text-[#16345c]">{euro(total)}</span>
                  </div>
                </div>

                <button type="submit" disabled={submitting} className="w-full bg-[#16345c] hover:bg-[#0d1b2f] text-white font-bold text-[14px] py-3 rounded-sm uppercase tracking-wide transition-colors disabled:opacity-60">
                  {submitting ? "Invio..." : "Invia trasferta"}
                </button>
              </form>
            </div>
          </div>

          {/* List */}
          <div className="lg:col-span-2">
            <h2 className="text-[16px] font-bold text-[#16345c] mb-4">Le mie trasferte</h2>
            {list.length === 0 ? (
              <div className="bg-white border border-gray-200 rounded-lg p-10 text-center text-gray-400">
                <Plane size={40} className="mx-auto mb-3 opacity-40" /> Nessuna trasferta
              </div>
            ) : (
              <div className="space-y-3">
                {list.map((t) => (
                  <div key={t.id} className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="text-[15px] font-bold text-[#16345c]">{t.destination}</span>
                          <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${TRIP_STATUS[t.status]?.cls}`}>{TRIP_STATUS[t.status]?.label}</span>
                        </div>
                        <div className="text-[13px] text-gray-600">{fmt(t.start_date)} → {fmt(t.end_date)}</div>
                        {t.reason && <div className="text-[12.5px] text-gray-400 mt-1 italic">"{t.reason}"</div>}
                      </div>
                      {["in_attesa_responsabile", "in_attesa_hr"].includes(t.status) && (
                        <button onClick={() => cancel(t.id)} className="shrink-0 flex items-center gap-1 text-[12px] font-semibold text-[#c00510] hover:bg-red-50 px-2 py-1 rounded-sm transition-colors"><X size={14} /> Annulla</button>
                      )}
                    </div>
                    {t.expenses?.length > 0 && (
                      <div className="mt-3 border-t border-gray-100 pt-2 space-y-1">
                        {t.expenses.map((ex, i) => (
                          <div key={`${t.id}-exp-${i}`} className="flex items-center justify-between text-[12.5px]">
                            <span className="text-gray-600 truncate">
                              <span className="inline-block text-[10px] font-bold bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded mr-1">{ex.category}</span>
                              {ex.description || "—"}
                              {ex.receipt_file_id && (
                                <button onClick={() => openFile(ex.receipt_file_id)} className="ml-1 text-[#0d6cb8] hover:underline inline-flex items-center gap-0.5"><Paperclip size={11} /></button>
                              )}
                            </span>
                            <span className="font-semibold text-gray-700 shrink-0">{euro(ex.amount)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
                      <span className="text-[12px] font-semibold text-gray-500">Totale rimborso</span>
                      <span className="text-[15px] font-black text-[#16345c]">{euro(t.total_expenses ?? t.estimated_amount)}</span>
                    </div>
                    {t.manager_decision && <div className="text-[12px] text-gray-500 mt-1"><b>Responsabile ({t.manager_decision.by}):</b> {t.manager_decision.note || "—"}</div>}
                    {t.hr_decision && <div className="text-[12px] text-gray-500"><b>HR ({t.hr_decision.by}):</b> {t.hr_decision.note || "—"}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default TrasfertePage;
